// ============================================
// Rich Text Payload Parsing
// ============================================
// Handles the [RICH_TEXT:<base64Delta>|<base64PlainText>] envelope format
// used to transmit Quill Delta documents as message payloads.

export interface ParsedRichText {
    /** The standard Quill Delta operations array containing formatting and content */
    delta: any[];
    /** Fallback plain-text content */
    plainText: string;
}

const RICH_TEXT_PREFIX = '[RICH_TEXT:';
const RICH_TEXT_SUFFIX = ']';

/**
 * Decodes a base64 encoded string using UTF-8 charset.
 * Compatible with both Node.js (Server Components) and Browser contexts.
 * Correctly preserves emojis and special unicode characters.
 */
function decodeBase64Utf8(base64Str: string): string {
    if (typeof window === 'undefined') {
        // Server-side (Node.js)
        return Buffer.from(base64Str, 'base64').toString('utf8');
    } else {
        // Client-side (Browser)
        const binaryString = atob(base64Str);
        const bytes = Uint8Array.from(binaryString, (char) => char.codePointAt(0)!);
        return new TextDecoder().decode(bytes);
    }
}

/**
 * Quick check whether a string matches the [RICH_TEXT:...|...] envelope pattern.
 * Does not perform full decoding — use for conditional branching before render.
 */
export function isRichTextPayload(text: string | undefined | null): boolean {
    if (!text) return false;
    return (
        text.startsWith(RICH_TEXT_PREFIX) &&
        text.endsWith(RICH_TEXT_SUFFIX) &&
        text.includes('|')
    );
}

/**
 * Validates and parses a message string into Quill Delta & plain text components.
 * Returns null if the format doesn't match the RICH_TEXT pattern.
 */
export function parseRichTextPayload(payload: string): ParsedRichText | null {
    if (!isRichTextPayload(payload)) {
        return null;
    }

    try {
        // Extract contents inside the envelope brackets
        const body = payload.substring(RICH_TEXT_PREFIX.length, payload.length - RICH_TEXT_SUFFIX.length);
        const separatorIndex = body.indexOf('|');
        if (separatorIndex === -1) return null;

        const encodedDelta = body.substring(0, separatorIndex);
        const encodedPlain = body.substring(separatorIndex + 1);

        const deltaJson = decodeBase64Utf8(encodedDelta);
        const plainText = decodeBase64Utf8(encodedPlain);

        const delta = JSON.parse(deltaJson);

        return {
            delta: Array.isArray(delta) ? delta : (delta.ops || []),
            plainText,
        };
    } catch (error) {
        console.error('Failed to parse rich text payload:', error);
        return null;
    }
}

/**
 * Extracts the plain text content from a rich text payload envelope.
 * If the input is not a rich text payload, returns the original string unchanged.
 * Useful for previews, search, and sidebar display where formatting is not needed.
 */
export function getRichTextPlainContent(text: string | undefined | null): string {
    if (!text) return '';
    if (!isRichTextPayload(text)) return text;

    const parsed = parseRichTextPayload(text);
    return parsed?.plainText || text;
}
